import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

interface InhousePdfViewerProps {
  url: string;
  fileName?: string;
  onClose?: () => void;
}

function appendPdfWindowStyles(documentRef: Document): void {
  const style = documentRef.createElement('style');
  style.textContent = `
    html, body {
      margin: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #2b2b2b;
      font-family: Arial, sans-serif;
    }
    .pdf-window {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }
    .pdf-toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 44px;
      padding: 0 12px;
      background: rgba(17, 24, 39, 0.96);
      color: #fff;
      box-sizing: border-box;
      flex-shrink: 0;
    }
    .pdf-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.82);
    }
    .pdf-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: 0;
      border-radius: 999px;
      color: #fff;
      background: transparent;
      cursor: pointer;
      font-size: 22px;
      line-height: 1;
    }
    .pdf-close:hover {
      background: rgba(255, 255, 255, 0.14);
    }
    embed {
      flex: 1;
      width: 100%;
      min-height: 0;
      border: 0;
    }
  `;
  documentRef.head.appendChild(style);
}

function openPdfViewerWindow(url: string, fileName?: string): void {
  const pdfUrl = new URL(url, window.location.origin).href;
  const viewerWindow = window.open('', '_blank');
  if (!viewerWindow) {
    window.open(pdfUrl, '_blank');
    return;
  }

  viewerWindow.document.title = fileName || 'PDF';
  appendPdfWindowStyles(viewerWindow.document);

  const root = viewerWindow.document.createElement('div');
  root.className = 'pdf-window';

  const toolbar = viewerWindow.document.createElement('div');
  toolbar.className = 'pdf-toolbar';

  const title = viewerWindow.document.createElement('div');
  title.className = 'pdf-title';
  title.textContent = fileName || 'PDF';

  const closeButton = viewerWindow.document.createElement('button');
  closeButton.className = 'pdf-close';
  closeButton.type = 'button';
  closeButton.title = 'Stäng PDF-fönster';
  closeButton.setAttribute('aria-label', 'Stäng PDF-fönster');
  closeButton.textContent = '×';
  closeButton.addEventListener('click', () => viewerWindow.close());

  const embed = viewerWindow.document.createElement('embed');
  embed.src = pdfUrl;
  embed.type = 'application/pdf';

  toolbar.append(title, closeButton);
  root.append(toolbar, embed);
  viewerWindow.document.body.appendChild(root);
  viewerWindow.document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') viewerWindow.close();
  });
}

const InhousePdfViewer: React.FC<InhousePdfViewerProps> = ({ url, fileName, onClose }) => {
  const [loading, setLoading] = useState(true);

  const handleOpenInNewTab = () => {
    openPdfViewerWindow(url, fileName);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%' }}>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 2,
          bgcolor: 'rgba(0,0,0,0.8)',
          borderRadius: 2,
          mb: 1,
          zIndex: 5,
          flexShrink: 0,
        }}
      >
        {fileName && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName}
          </Typography>
        )}

        <Box sx={{ width: 1, height: 24, bgcolor: 'rgba(255,255,255,0.3)', mx: 0.5 }} />

        <Tooltip title="Öppna i nytt fönster">
          <IconButton size="small" onClick={handleOpenInNewTab} sx={{ color: '#fff' }}>
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ladda ner">
          <IconButton size="small" component="a" href={url} download={fileName || 'dokument.pdf'} sx={{ color: '#fff' }}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        {onClose && (
          <Tooltip title="Stäng">
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* PDF via browser's built-in viewer */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          position: 'relative',
          minHeight: 0,
        }}
      >
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        )}
        <embed
          src={url}
          type="application/pdf"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: 8,
          }}
          onLoad={() => setLoading(false)}
        />
      </Box>
    </Box>
  );
};

export default InhousePdfViewer;
